import React, { useState } from "react";
import { X, AlertTriangle } from "lucide-react";
import { Button } from "../ui/button";
import { pipelineApi } from "../../lib/api";
import { formatApiErrorDetail } from "../../contexts/AuthContext";

const COLORS = ["#2563EB", "#7C3AED", "#059669", "#DC2626", "#D97706", "#0891B2", "#DB2777", "#475569"];

export default function NewPipelineModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [color, setColor] = useState(COLORS[0]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Pipeline name is required");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const pipe = await pipelineApi.createPipeline({ name: name.trim(), color });
      onCreated(pipe);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Could not create pipeline");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-6">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-slate-200" data-testid="new-pipeline-modal">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <div className="text-[14px] font-semibold text-slate-900">New pipeline</div>
          <button onClick={onClose} data-testid="new-pipeline-close" className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4" data-testid="new-pipeline-form">
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">Pipeline name</label>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="new-pipeline-name"
              placeholder="e.g. Leads, Maintenance Contracts"
              className="w-full h-10 px-3 rounded-lg border border-slate-200 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
            />
            <p className="text-[11.5px] text-slate-400 mt-1.5">Starts with 4 columns (New · Working · Won · Lost) you can customize.</p>
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">Color</label>
            <div className="flex items-center gap-2 flex-wrap">
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  data-testid={`new-pipeline-color-${c.replace("#", "")}`}
                  style={{ backgroundColor: c }}
                  className={`h-7 w-7 rounded-full transition-transform ${color === c ? "ring-2 ring-offset-2 ring-slate-400 scale-110" : ""}`}
                />
              ))}
            </div>
          </div>
          {error && (
            <div data-testid="new-pipeline-error" className="px-3 py-2 rounded-md bg-rose-50 border border-rose-200 text-[12.5px] text-rose-700 flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5" /> {error}
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose} className="h-9 text-[13px]">Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="new-pipeline-submit" className="h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[13px]">
              {busy ? "Creating…" : "Create pipeline"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
